import MasterComponent from "../../../components/Forms"
import moment from 'moment';
import { MasterConfigs, FormColumnConfig } from "../../../interfaces/entity";
import { Button, Card, Flex, Modal, notification, Typography, Badge as AntBadge, Space, Radio, Form, Select, Input, Tag, Dropdown } from "antd";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import { formatPriceIndian } from "../../../constants/functions";
import PdfView from "../../../components/custom/PDFView";
import Badge from "../../../components/Badge";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { constants } from "../../../constants/common";
import DrawerComponent from "../../../components/Forms/Drawer";
import InvoiceDetails from "../../Leads/Invoices/InvoiceDetails";
import { SendOutlined, SyncOutlined } from "@ant-design/icons";
import { postData } from "../../../actions/developer";
import { FormInstance } from "antd/lib";
import dayjs from "dayjs";
import InvoiceSummary from "../../../components/InvoiceSummary";

const { Title, Text } = Typography;

const PendingInvoicesToSend = () => {
    const [pdfURL, setPDFURL] = React.useState<any>(null);
    const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [dealValue, setDealValue] = useState(null);
    const [dealData, setDealData] = useState(null);
    const [isContract, setIsContract] = useState(false);
    const [summary, setSummary] = useState<any>(null);
    const [form, setForm] = useState<FormInstance>(null);
    const [isUpdate, setIsUpdate] = useState<any>(null);
    const dispatch = useDispatch();

    const handleLeadClick = (record) => {
        setSelectedInvoice(record);
        setOpenDrawer(true);
    }

    const onClose = () => {
        setDealValue(null);
        setDealData(null);
        setIsContract(false);
        setForm(null);
        setIsUpdate(false);
        setPDFURL(null);
        setSelectedInvoice(null);
    }

    const getDealvalue = async (dealId, skipFormUpdate = false) => {
        if (!dealId) {
            return;
        }
        try {
            const resData = await dispatch(getData(`/deals/${dealId}`))
            if (resData?.code == 200) {
                const invoiceSettings = await dispatch(getData('/invoices/settings'));
                if (invoiceSettings.code == 200) {
                    const isContractField = invoiceSettings?.data?.contractType === resData?.data?.leadType?._id
                    setIsContract(isContractField);
                    // Only populate form if not updating (creating new) and skipFormUpdate is false
                    if (!isUpdate && form && !skipFormUpdate && isContractField) {
                        const isHourlyBilling = resData?.data?.billingType === 'Hourly';
                        const developerData: any = {
                            developers: resData?.data?.developer?.firstName || '',
                            price: resData?.data?.amount || '', // Use amount field for both hourly rate and monthly amount
                            includeSaturday: false,
                            countAsWorkingDays: true,
                        };

                        if (isHourlyBilling) {
                            // Hourly billing - set hours
                            developerData.hours = 180; // Default hours
                            developerData.leaves = 0;
                            developerData.extraDays = 0;
                        } else {
                            // Monthly billing - set dates
                            developerData.leaves = 0;
                            developerData.extraDays = 0;
                            developerData.startDate = dayjs(moment().subtract(1, 'months').startOf('month').toDate());
                            developerData.endDate = dayjs(moment().subtract(1, 'months').endOf('month').toDate());
                        }

                        form.setFieldsValue({
                            developers: [developerData],
                            tds: resData?.data?.tds || 10,
                            GST: resData?.data?.gst || 18,
                        });
                    } else if (!isUpdate && form && !skipFormUpdate && !isContractField) {
                        form.setFieldsValue({
                            project: [{
                                projectName: resData?.data?.project?.name || '',
                                price: resData?.data?.amount || '',
                            }],
                            tds: resData?.data?.tds || 10,
                            GST: resData?.data?.gst || 18,
                        });
                    }
                } else {
                    setIsContract(false);
                }
                setDealData(resData?.data)
            }
        } catch (error) {
            console.log(error)
        }
    }

    const getObject = (isContract) => {
        if (isContract) {
            return [
                {
                    name: 'developers',
                    type: 'form',
                    span: 24,
                    form: [
                        {
                            label: 'Developer',
                            name: 'developers',
                            type: 'text',
                            rules: [{
                                required: true,
                                message: 'Developer is required',
                            }],
                            span: 8,
                        },
                        {
                            label: 'Price',
                            name: 'price',
                            type: 'number',
                            rules: [
                                {
                                    required: true,
                                    message: 'Price is required',
                                },
                                {
                                    validator: (_, value) => {
                                        if (value && isNaN(value)) {
                                            return Promise.reject(new Error('Price must be a number'));
                                        }
                                        return Promise.resolve();
                                    },
                                }],
                            span: 8,
                        },
                        {
                            label: 'Hours',
                            name: 'hours',
                            type: 'number',
                            span: 8,
                            placeholder: 'Enter hours (for hourly billing)',
                        },
                        {
                            label: 'Leaves',
                            name: 'leaves',
                            type: 'number',
                            span: 8,
                        },
                        {
                            label: 'Extra Days',
                            name: 'extraDays',
                            type: 'number',
                            span: 8,
                            placeholder: 'Enter extra days worked',
                        },
                        {
                            label: 'Start Date',
                            name: 'startDate',
                            type: 'date',
                            rules: [{
                                validator: (_, value) => {
                                    const hours = form?.getFieldValue(['developers', 0, 'hours']);
                                    // Not required if hours is provided (hourly billing)
                                    if (hours) {
                                        return Promise.resolve();
                                    }
                                    // Required for monthly billing
                                    if (!value) {
                                        return Promise.reject(new Error('Start Date is required for monthly billing'));
                                    }
                                    return Promise.resolve();
                                },
                            }],
                            dateProps: {
                                format: 'DD-MM-YYYY',
                            },
                            span: 8,
                        },
                        {
                            label: 'End Date',
                            name: 'endDate',
                            rules: [{
                                validator: (_, value) => {
                                    const hours = form?.getFieldValue(['developers', 0, 'hours']);
                                    // Not required if hours is provided (hourly billing)
                                    if (hours) {
                                        return Promise.resolve();
                                    }
                                    // Required for monthly billing
                                    if (!value) {
                                        return Promise.reject(new Error('End Date is required for monthly billing'));
                                    }
                                    return Promise.resolve();
                                },
                            }],
                            dateProps: {
                                format: 'DD-MM-YYYY',
                            },
                            type: 'date',
                            span: 8,
                        },
                        {
                            span: 8,
                        },
                        {
                            label: 'Saturday Working',
                            name: 'includeSaturday',
                            type: 'checkbox',
                            span: 8,
                        },
                        {
                            label: 'Is Billing for Working days?',
                            name: 'countAsWorkingDays',
                            type: 'checkbox',
                            span: 8,
                        },
                    ],
                }
            ];
        } else {
            return [
                {
                    name: 'project',
                    type: 'form',
                    span: 24,
                    form: [
                        {
                            label: 'Project Name',
                            name: 'projectName',
                            type: 'text',
                            rules: [{
                                required: true,
                                message: 'Project Name is required',
                            }],
                            span: 8,
                        },
                        {
                            label: 'Milestones',
                            name: 'milestones',
                            type: 'text',
                            rules: [{
                                required: true,
                                message: 'Milestones is required',
                            }],
                            span: 8,
                        },
                        {
                            label: 'Price',
                            name: 'price',
                            rules: [{
                                required: true,
                                message: 'Price is required',
                            },
                            {
                                validator: (_, value) => {
                                    if (value && isNaN(value)) {
                                        return Promise.reject(new Error('Price must be a number'));
                                    }
                                    return Promise.resolve();
                                },
                            }],
                            type: 'number',
                            span: 8,
                        }
                    ],
                }
            ];
        }
    };

    const onSyncInvoices = async () => {
        try {
            setSyncing(true);
            const resData = await dispatch(postData('/invoiceJobs/sync/generate-invoices', {}));
            if (resData?.code == 200) {
                const data = resData?.data || {};
                const invoicesCreated = data.invoicesCreated || 0;
                const dealsProcessed = data.dealsProcessed || 0;
                const suggestedCount = data.suggestedInvoicesCount || 0;

                const description = [
                    data.message || 'Invoice generation sync completed successfully',
                    `Invoices Created: ${invoicesCreated}`,
                    `Deals Processed: ${dealsProcessed}`,
                    `Suggested Invoices Available: ${suggestedCount}`
                ].join(' | ');

                notification.success({
                    message: 'Sync Completed',
                    description: description,
                    duration: 5,
                });

                if (data.errors && data.errors.length > 0) {
                    notification.warning({
                        message: 'Some Errors Occurred',
                        description: `${data.errors.length} error(s) occurred during sync. Check console for details.`,
                        duration: 5,
                    });
                }

                // Refresh the table after a short delay to allow backend to process
                setTimeout(() => {
                    dispatch({
                        type: 'REFRESH_TABLE',
                        refreshTable: true
                    });
                }, 1000);
            } else {
                notification.error({
                    message: 'Error',
                    description: resData?.message || 'Failed to sync invoice generation',
                });
            }
        } catch (error) {
            console.error(error);
            notification.error({
                message: 'Error',
                description: 'Failed to sync invoice generation',
            });
        } finally {
            setSyncing(false);
        }
    }

    const onSendInvoice = async () => {
        try {
            if (!selectedInvoice) {
                return
            }
            setLoading(true);
            const resData = await dispatch(getData(`/invoiceJobs/send/mail/${selectedInvoice?._id}`));
            if (resData?.code == 200) {
                notification.success({
                    message: 'Success',
                    description: 'Invoice sent successfully',
                });
                // Close modal
                setPDFURL(null);
                setSelectedInvoice(null);
                // Refresh the table
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true
                });
            }
        } catch (error) {
            console.error(error);
            notification.error({
                message: 'Error',
                description: 'Failed to send invoice',
            });
        } finally {
            setLoading(false);
        }
    }

    const invoiceConfig: MasterConfigs = useMemo(() => ({
        api: () => '/invoiceJobs/suggested',
        title: 'Suggested Invoices',
        isActionTab: true,
        maskClosable: false,
        onClose: onClose,
        modalTitle: (action) => action + ' Invoice',
        onDataLoad: async (e) => {
            if (e?.billTo) {
                setIsUpdate(true);
                // Determine if it's a contract invoice from the data itself
                // Check if developers array exists and has data (contract) vs project array (fix bid)
                const isContractInvoice = e?.developers && Array.isArray(e.developers) && e.developers.length > 0;
                // Set isContract immediately so form columns can be determined correctly
                setIsContract(isContractInvoice);

                // Transform developers/project data for form if editing
                if (form) {
                    // Wait a bit for isContract state to update and form to re-render
                    setTimeout(() => {
                        if (isContractInvoice && e.developers) {
                            // Transform developers array - convert developer objects to strings if needed
                            const transformedDevelopers = e.developers.map((dev: any) => {
                                const developerObj = typeof dev.developers === 'object' && dev.developers !== null
                                    ? dev.developers
                                    : { firstName: dev.developers || '' };

                                return {
                                    developers: developerObj.firstName || developerObj.name || dev.developers || '',
                                    price: dev.price || '',
                                    hours: dev.hours || null,
                                    leaves: dev.leaves || '0',
                                    extraDays: dev.extraDays || 0,
                                    startDate: dev.startDate ? dayjs(dev.startDate) : null,
                                    endDate: dev.endDate ? dayjs(dev.endDate) : null,
                                    includeSaturday: dev.includeSaturday || false,
                                    countAsWorkingDays: dev.countAsWorkingDays !== undefined ? dev.countAsWorkingDays : true,
                                };
                            });

                            form.setFieldsValue({
                                developers: transformedDevelopers,
                            });
                        } else if (!isContractInvoice && e.project) {
                            // Transform project array
                            const transformedProjects = e.project.map((proj: any) => ({
                                projectName: proj.projectName || '',
                                milestones: proj.milestones || '',
                                price: proj.price || '',
                            }));

                            form.setFieldsValue({
                                project: transformedProjects,
                            });
                        }
                    }, 100);
                }

                // When editing, form values are already loaded by patchData
                // We only need to fetch deal data for contract type detection, but skip form value updates
                // Use setTimeout to ensure state update happens before form re-renders
                setTimeout(() => {
                    getDealvalue(e?.billTo, true);
                }, 0);
            }
        },
        setForm: (e) => setForm(e),
        onSummaryLoad: (summaryData) => {
            setSummary(summaryData);
        },
        renderSummary: () => {
            return <InvoiceSummary summary={summary} />;
        },
        getFilterColumns: () => {
            return [
                {
                    label: 'Account (Client Company)',
                    name: 'billToFilter',
                    type: 'select',
                    isCombo: true,
                    span: 6,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                        placeholder: 'Select Account',
                    },
                },
                {
                    label: 'Developer',
                    name: 'developer',
                    type: 'text',
                    span: 6,
                    placeholder: 'Search by developer name',
                },
                {
                    type: 'custom',
                    span: 6,
                    formItem: (form) => {
                        const InvoiceStatusFilter = () => {
                            return (
                                <Form.Item label="Invoice Status" name="invoiceStatus">
                                    <Space.Compact style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Form.Item name="invoiceStatusFilterType" initialValue="include" style={{ margin: 0, width: '120px' }}>
                                            <Select style={{ width: '120px' }}>
                                                <Select.Option value="include">Include</Select.Option>
                                                <Select.Option value="exclude">Exclude</Select.Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item name="invoiceStatus" style={{ margin: 0, flex: 1 }}>
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                placeholder="Select Status"
                                                style={{ width: '100%' }}
                                                maxTagCount="responsive"
                                            >
                                                <Select.Option value="draft">Draft</Select.Option>
                                                <Select.Option value="sent">Sent</Select.Option>
                                                <Select.Option value="paid">Paid</Select.Option>
                                                <Select.Option value="partially_paid">Partially Paid</Select.Option>
                                                <Select.Option value="overdue">Overdue</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            );
                        };
                        return <InvoiceStatusFilter />;
                    },
                },
                {
                    label: 'Invoice Month',
                    name: 'invoiceMonth',
                    type: 'date',
                    span: 6,
                    dateProps: {
                        picker: 'month',
                        format: 'MMM YYYY',
                        allowClear: true,
                        placeholder: 'Select Month',
                    },
                },
                {
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 6,
                    dateProps: {
                        format: 'DD MMM, YYYY',
                        allowClear: true,
                        placeholder: 'Select Start Date',
                    },
                },
                {
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 6,
                    dateProps: {
                        format: 'DD MMM, YYYY',
                        allowClear: true,
                        placeholder: 'Select End Date',
                    },
                },
                {
                    label: 'Due Date From',
                    name: 'dueDateFrom',
                    type: 'date',
                    span: 6,
                    dateProps: {
                        format: 'DD-MM-YYYY',
                        allowClear: true,
                        placeholder: 'Due Date From',
                    },
                },
                {
                    label: 'Due Date To',
                    name: 'dueDateTo',
                    type: 'date',
                    span: 6,
                    dateProps: {
                        format: 'DD-MM-YYYY',
                        allowClear: true,
                        placeholder: 'Due Date To',
                    },
                },
            ];
        },
        getColumns: () => {
            return [
                {
                    title: 'Invoice Number',
                    dataIndex: 'invoiceNumber',
                    key: 'invoiceNumber',
                    width: 150,
                    fixed: 'left',
                    align: 'left',
                    render: (value, records) => {
                        // Get start and end dates from developers array
                        let startDate = null;
                        let endDate = null;

                        if (records?.developers && Array.isArray(records.developers) && records.developers.length > 0) {
                            const dates = records.developers
                                .filter(dev => dev.startDate || dev.endDate)
                                .map(dev => ({
                                    start: dev.startDate ? moment(dev.startDate) : null,
                                    end: dev.endDate ? moment(dev.endDate) : null
                                }));

                            if (dates.length > 0) {
                                const validStarts = dates.map(d => d.start).filter(d => d && d.isValid());
                                const validEnds = dates.map(d => d.end).filter(d => d && d.isValid());

                                if (validStarts.length > 0) {
                                    startDate = moment.min(validStarts);
                                }
                                if (validEnds.length > 0) {
                                    endDate = moment.max(validEnds);
                                }
                            }
                        }

                        return (
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ textAlign: 'left' }}>
                                    <Button type="link" className="text-left !p-0" size="small" onClick={() => handleLeadClick(records)}>{value}</Button>
                                </div>
                                {(startDate || endDate) && (
                                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '-2px' }}>
                                        {startDate && endDate
                                            ? `${startDate.format('DD MMM')} - ${endDate.format('DD MMM')}, ${endDate.format('YYYY')}`
                                            : startDate
                                                ? `From: ${startDate.format('DD MMM, YYYY')}`
                                                : endDate
                                                    ? `To: ${endDate.format('DD MMM, YYYY')}`
                                                    : ''
                                        }
                                    </div>
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: 'Bill TO',
                    dataIndex: 'billTo',
                    key: 'billTo',
                    fixed: 'left',
                    width: 200,
                    render: (value) => {
                        if (!value) return '-';
                        return (
                            <div>
                                <div>{value.dealName || '-'}</div>
                                {value?.business?.businessName && (
                                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                                        {value?.business?.businessName}
                                    </div>
                                )}
                            </div>
                        );
                    },
                },
                {
                    title: 'GST Amount',
                    dataIndex: 'gstAmount',
                    key: 'gstAmount',
                    width: 150,
                    render: (value) => {
                        return value ? `₹ ${formatPriceIndian(value)}` : '-';
                    }
                },
                {
                    title: 'TDS Amount',
                    dataIndex: 'tdsAmount',
                    key: 'tdsAmount',
                    width: 150,
                    render: (value) => {
                        return value ? `₹ ${formatPriceIndian(value)}` : '-';
                    }
                },
                {
                    title: 'Bill Amount',
                    dataIndex: 'totalBill',
                    key: 'totalBill',
                    width: 150,
                    render: (value) => {
                        return value ? `₹ ${formatPriceIndian(value)}` : '-';
                    }
                },
                {
                    title: 'Invoice Status',
                    dataIndex: 'invoiceStatus',
                    key: 'invoiceStatus',
                    align: 'center',
                    width: 150,
                    render: (value) => {
                        if (value === 'draft') {
                            return <Badge color="blue" text="Draft" />;
                        }
                        if (value === 'sent') {
                            return <Badge color="orange" text="Sent" />;
                        }
                        if (value === 'paid') {
                            return <Badge color="green" text="Paid" />;
                        }
                        if (value === 'cancelled') {
                            return <Badge color="red" text="Cancelled" />;
                        }
                        if (value === 'overdue') {
                            return <Badge color="red" text="Overdue" />;
                        }
                        if (value === 'partially_paid') {
                            return <Badge color="yellow" text="Partially Paid" />;
                        }
                        if (value === 'refunded') {
                            return <Badge color="purple" text="Refunded" />;
                        }
                        if (value === 'void') {
                            return <Badge color="gray" text="Void" />;
                        }
                        return <Badge color="gray" text={value || 'N/A'} />;
                    }
                },
                {
                    title: 'Invoice Date',
                    dataIndex: 'invoiceDate',
                    key: 'invoiceDate',
                    align: 'center',
                    width: 150,
                    render: (value) => {
                        return value ? moment(value).format('DD MMM, YYYY') : '-';
                    },
                },
                {
                    title: 'Due Date',
                    dataIndex: 'dueDate',
                    key: 'dueDate',
                    align: 'center',
                    width: 150,
                    render: (value, record) => {
                        if (!value) return '-';
                        const isToday = moment(value).isSame(moment(), 'day');
                        const isOverdue = moment(value).isBefore(moment(), 'day');
                        if (isToday) {
                            return <AntBadge status="warning" text="Due Today" />;
                        }
                        if (isOverdue) {
                            return <AntBadge status="error" text={`Overdue by ${moment(value).fromNow()}`} />;
                        }
                        return moment(value).format('DD MMM, YYYY');
                    },
                },
                {
                    title: 'Action',
                    key: 'action',
                    align: 'center',
                    width: 200,
                    fixed: 'right',
                    render: (_, record) => {
                        return (
                            <Flex gap={8} justify="center">
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={() => {
                                        setPDFURL(record.invoiceFile);
                                        setSelectedInvoice(record);
                                    }}
                                    disabled={!record.invoiceFile}
                                >
                                    Send
                                </Button>
                            </Flex>
                        );
                    },
                },
                {
                    title: 'Invoice Owner',
                    dataIndex: 'invoiceOwner',
                    key: 'invoiceOwner',
                    align: 'center',
                    width: 150,
                    render: (value) => {
                        return value ? <SmallProfileCard
                            name={constants.GET_FULL_NAME(value)}
                            avatar={value?.avatar}
                        /> : 'No Owner';
                    },
                }
            ];
        },
        getFormColumns: () => {
            return [
                {
                    label: 'Bill From',
                    name: 'billFrom',
                    type: 'select',
                    isCombo: true,
                    rules: [
                        {
                            required: true,
                            message: 'Please select a Bill From',
                        },
                    ],
                },
                {
                    label: 'Bill To',
                    name: 'billTo',
                    type: 'select',
                    isCombo: true,
                    selectProps: {
                        onChange: (e) => {
                            getDealvalue(e);
                        },
                    },
                    rules: [
                        {
                            required: true,
                            message: 'Please select a Bill To',
                        },
                    ],
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (text) => <Title level={5}>Billing Information</Title>
                },
                ...getObject(isContract),
                {
                    type: 'custom',
                    span: 24,
                    formItem: (text) => <Title level={5}>Taxes</Title>
                },
                {
                    label: 'TDS',
                    name: 'tds',
                    lebelInstructions: 'Value will be count as percentage(%)',
                    type: 'number',
                },
                {
                    label: 'GST',
                    name: 'GST',
                    lebelInstructions: 'Value will be count as percentage(%)',
                    type: 'number',
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (text) => <Title level={5}>Non Taxable (Bonus / Commission)</Title>
                },
                {
                    name: 'nonTaxable',
                    type: 'form',
                    span: 24,
                    form: [
                        {
                            label: 'Component Name',
                            name: 'componentName',
                            type: 'text',
                            span: 8,
                        },
                        {
                            label: 'Amount',
                            name: 'amount',
                            type: 'text',
                            rules: [{
                                validator: (_, value) => {
                                    if (value && isNaN(value)) {
                                        return Promise.reject(new Error('Amount must be a number'));
                                    } else {
                                        return Promise.resolve();
                                    }
                                }
                            }],
                            span: 8,
                        },
                    ]
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (text) => <Title level={5}>Gross Deductions</Title>
                },
                {
                    name: 'grossDeductions',
                    type: 'form',
                    span: 24,
                    form: [
                        {
                            label: 'Component Name',
                            name: 'componentName',
                            type: 'text',
                            span: 8,
                        },
                        {
                            label: 'Amount',
                            name: 'amount',
                            type: 'text',
                            rules: [{
                                validator: (_, value) => {
                                    if (value && isNaN(value)) {
                                        return Promise.reject(new Error('Amount must be a number'));
                                    } else {
                                        return Promise.resolve();
                                    }
                                }
                            }],
                            span: 8,
                        },
                    ]
                },
                {
                    label: 'Invoice Date',
                    name: 'invoiceDate',
                    type: 'date',
                    dateProps: {
                        format: 'DD-MM-YYYY',
                        onChange: (date, dateString) => {
                            if (date && form) {
                                form.setFieldsValue({ dueDate: dayjs(date).add(7, 'day') });
                            }
                        },
                    },
                },
                {
                    label: 'Due Date',
                    name: 'dueDate',
                    type: 'date',
                    dateProps: {
                        format: 'DD-MM-YYYY',
                    },
                },
                {
                    label: 'Timesheet',
                    name: 'timesheet',
                    type: 'file',
                    multiple: true,
                },
            ].filter(Boolean) as FormColumnConfig[];
        },
        combo: [
            {
                name: 'billTo',
                api: '/invoiceJobs/won-deals',
                idKey: '_id',
                render: (text) => (
                    <div>{text.dealName} - {text.businessName}</div>
                ),
            },
            {
                name: 'billToFilter',
                api: '/invoiceJobs/accounts',
                idKey: '_id',
                render: (text) => text.businessName || text.name || '',
            },
            {
                name: 'billFrom',
                api: '/bank-accounts',
                idKey: '_id',
                render: (text) => <>{text.beneficiaryName} - ({text.ifscNumber})</>
            },
        ],
        getData: () => ({}),
        actions: [
            {
                actionType: 'filter',
                name: 'Filter',
            },
            {
                actionType: 'Custom',
                name: 'Sync Invoices',
                render: () => (
                    <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={syncing}
                        onClick={onSyncInvoices}
                    >
                        Sync Invoices
                    </Button>
                )
            }
        ]
    }), [syncing, isContract, dealValue, summary]);

    return <>
        <Modal
            title={
                <Flex align="center" gap={8}>
                    <SendOutlined />
                    <span>Send Invoice - {selectedInvoice?.invoiceNumber}</span>
                </Flex>
            }
            open={!!pdfURL}
            onCancel={() => {
                setPDFURL(null);
                setSelectedInvoice(null);
            }}
            width={1000}
            onOk={() => onSendInvoice()}
            okButtonProps={{ loading: loading }}
            okText={"Send Mail"}
            cancelText="Cancel"
        >
            <PdfView url={process.env.FILE_URL + pdfURL} isToolbar={true} />
        </Modal>
        <MasterComponent config={invoiceConfig} />
        <DrawerComponent
            title={
                <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                    {selectedInvoice?.invoiceNumber}
                </Flex>
            }
            visible={openDrawer}
            handleCancel={() => setOpenDrawer(false)}
            isView={true}
            loading={false}
            submitForm={() => { }}
            rest={{
                width: '90%', style: {
                    background: '#eef1f9',
                    padding: '0px !important',
                },
                bodyStyle: { padding: 0 },
                headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
            }}
        >
            <InvoiceDetails invoice={selectedInvoice} />
        </DrawerComponent>
    </>
}

export default PendingInvoicesToSend;
